import React, { useState, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Flex,
  Grid,
  SimpleGrid,
  Text,
  Button,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  Icon,
  Badge,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  TagCloseButton,
  keyframes,
  useToast,
} from "@chakra-ui/react";
import { FaCheck, FaArrowRight, FaArrowLeft, FaPlus, FaFileAlt, FaPaperPlane, FaSearch, FaRegClock, FaUpload, FaFilePdf } from "react-icons/fa";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import IconBox from "components/Icons/IconBox";
import {
  CANAUX,
  FORMATS,
  FREQUENCES,
  JOURS,
  canal,
  format as fmt,
  frequence as freq,
  jour,
  genererMapping,
} from "variables/veille";
import { useVeilles, requestMatrice, requestAnalyse, fetchAnalyseJob, fetchVeilleConfig } from "contexts/VeillesContext";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: translateY(0); }
`;

const STEPS = ["Votre besoin", "Cadrage de la veille", "Format & diffusion", "Document de mapping"];
const DESCS = [
  "Client, marque, ce qu'il faut suivre",
  "Secteur, mots-clés et canaux",
  "Format, fréquence et destinataires",
  "Le document utilisé pour veiller",
];
const EXEMPLE =
  "Nous sommes NordVPN. Nous voulons suivre notre réputation autour de la protection des données et de la vie privée, détecter toute mention de faille ou de fuite, et surveiller ce qui se dit sur les VPN gratuits concurrents.";
const HEURES = ["06:00", "07:00", "08:00", "09:00", "12:00", "18:00"];

export default function NouvelleVeille() {
  const history = useHistory();
  const location = useLocation();
  const toast = useToast();

  // Mode édition : ?edit=<id> réaffiche tout le wizard prérempli avec la veille existante.
  const { getVeille, updateVeilleComplete, addVeille } = useVeilles();
  const editId = new URLSearchParams(location.search).get("edit");
  const veilleEditee = editId ? getVeille(editId) : null;
  const edition = Boolean(editId);

  const [step, setStep] = useState(0);
  const [nom, setNom] = useState("");
  const [client, setClient] = useState("");
  const [demande, setDemande] = useState("");
  const [analyzed, setAnalyzed] = useState(false);
  const [analyseEnCours, setAnalyseEnCours] = useState(false);
  const [secteur, setSecteur] = useState("");
  const [motsCles, setMotsCles] = useState([]);
  const [nouveauMot, setNouveauMot] = useState("");
  const [canaux, setCanaux] = useState([]);
  const [sources, setSources] = useState([]);          // sources spécifiques nommées par l'utilisateur
  const [nouvelleSource, setNouvelleSource] = useState("");
  const [formatId, setFormatId] = useState("synthese");
  const [frequenceId, setFrequenceId] = useState("hebdomadaire");
  const [jours, setJours] = useState(["lun"]);          // jours d'envoi choisis
  const [destinataires, setDestinataires] = useState(["camille@agence-veille.fr"]);
  const [nouveauDest, setNouveauDest] = useState("");
  const [heure, setHeure] = useState("08:00");
  const [mappingDoc, setMappingDoc] = useState("");
  const [mappingEdite, setMappingEdite] = useState(false);
  const [fichier, setFichier] = useState(null);
  const fileRef = useRef(null);

  // Préremplissage en mode édition : reporte toutes les valeurs existantes dans le wizard
  // (une seule fois, quand la veille est disponible) pour que tout reste modifiable.
  const [prerempli, setPrerempli] = useState(false);
  useEffect(() => {
    if (!veilleEditee || prerempli) return;
    setNom(veilleEditee.nom || "");
    setClient(veilleEditee.client || "");
    setSecteur(veilleEditee.secteur || "");
    setDemande(veilleEditee.demande || "");
    setMotsCles(veilleEditee.motsCles || []);
    setCanaux(veilleEditee.canaux || []);
    setFormatId(veilleEditee.format || "synthese");
    setFrequenceId(veilleEditee.frequence || "hebdomadaire");
    setAnalyzed(true);
    setPrerempli(true);
    // Charge la config RÉELLE (destinataires, mapping, heure, jours) pour ne rien écraser au save.
    fetchVeilleConfig(veilleEditee.id).then((cfg) => {
      if (cfg.destinataires.length) setDestinataires(cfg.destinataires);
      if (cfg.jours.length) setJours(cfg.jours);
      if (cfg.heure) setHeure(cfg.heure);
      if (cfg.mapping) { setMappingDoc(cfg.mapping); setMappingEdite(true); }
    });
  }, [veilleEditee, prerempli]);

  const donnees = { nom, client, secteur, demande, motsCles, canaux, sources, format: formatId, frequence: frequenceId, destinataires, jours, heure };

  // Régénère le document de mapping à l'arrivée sur l'étape 4, sauf si l'utilisateur
  // l'a édité à la main ou a importé son propre fichier (sources mutuellement exclusives).
  useEffect(() => {
    if (step === 3 && !mappingEdite && !fichier) {
      setMappingDoc(genererMapping(donnees));
    }
  }, [step]); // eslint-disable-line

  function onFichier(e) {
    const f = e.target.files && e.target.files[0];
    if (f) {
      setFichier(f.name);
      setMappingEdite(false);
      toast({ title: "Document importé", description: `« ${f.name} » remplace le document généré.`, status: "success", duration: 2800 });
    }
  }
  function continuer() {
    const next = Math.min(STEPS.length - 1, step + 1);
    setStep(next);
  }

  // Analyse IA réelle de la demande (job headless traité par le moteur, sur l'abonnement).
  // Remplace l'ancien moteur de règles par mots-clés (qui confondait « assurance » et « banque »).
  async function analyser() {
    const src = `${demande} ${client} ${nom}`.trim();
    if (src.length < 3) {
      toast({ title: "Décrivez d'abord votre besoin de veille.", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    setAnalyseEnCours(true);
    try {
      const { jobId, error } = await requestAnalyse(demande || src, client);
      if (error || !jobId) throw new Error((error && error.message) || "job d'analyse non créé");
      let res = null;
      for (let i = 0; i < 40 && !res; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const job = await fetchAnalyseJob(jobId);
        if (job && job.statut === "termine") res = job.resultat;
        else if (job && job.statut === "echec") throw new Error(job.erreur || "analyse en échec");
      }
      if (!res) throw new Error("délai d'analyse dépassé");
      if (res.secteur) setSecteur(res.secteur);
      if (Array.isArray(res.mots_cles) && res.mots_cles.length) setMotsCles(res.mots_cles);
      if (Array.isArray(res.canaux) && res.canaux.length) setCanaux(res.canaux);
      setAnalyzed(true);
      setStep(1);
      toast({ title: "Demande analysée", description: res.resume || "Cadrage proposé — ajustez si besoin.", status: "success", duration: 4500, isClosable: true });
    } catch (e) {
      toast({ title: "Analyse indisponible", description: `${e.message}. Renseignez le cadrage à la main, puis continuez.`, status: "warning", duration: 5000, isClosable: true });
      setStep(1);
    } finally {
      setAnalyseEnCours(false);
    }
  }
  function toggleCanal(id) {
    setCanaux((p) => (p.includes(id) ? p.filter((c) => c !== id) : [...p, id]));
  }
  function ajouterMot() {
    const m = nouveauMot.trim();
    if (m && !motsCles.includes(m)) setMotsCles((p) => [...p, m]);
    setNouveauMot("");
  }
  function ajouterSource() {
    const s = nouvelleSource.trim();
    if (s && !sources.includes(s)) setSources((p) => [...p, s]);
    setNouvelleSource("");
  }
  function toggleJour(id) {
    setJours((p) => (p.includes(id) ? p.filter((j) => j !== id) : [...p, id]));
  }
  function ajouterDest() {
    const e = nouveauDest.trim();
    if (!e) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) return toast({ title: "Adresse e-mail invalide.", status: "warning", duration: 2000 });
    if (!destinataires.includes(e)) setDestinataires((p) => [...p, e]);
    setNouveauDest("");
  }
  // Choisir un rythme propose des jours par défaut, que l'utilisateur reste libre d'ajuster.
  function choisirFrequence(id) {
    setFrequenceId(id);
    const def = (freq(id) || {}).jours || [];
    setJours(def);
  }
  async function creer(avecMatrice = false) {
    if (!nom.trim()) return toast({ title: "Donnez un nom à votre veille.", status: "warning", duration: 3000 });
    if (motsCles.length === 0) return toast({ title: "Ajoutez au moins un mot-clé.", status: "warning", duration: 3000 });
    if (canaux.length === 0) return toast({ title: "Sélectionnez au moins un canal.", status: "warning", duration: 3000 });
    if (frequenceId !== "temps_reel" && jours.length === 0) return toast({ title: "Choisissez au moins un jour d'envoi.", status: "warning", duration: 3000 });
    if (destinataires.length === 0) return toast({ title: "Ajoutez au moins un destinataire.", status: "warning", duration: 3000 });
    const patch = { nom, client, secteur, demande, motsCles, canaux, sources, format: formatId, frequence: frequenceId, jours, destinataires, heure, mapping: mappingDoc };
    if (edition && veilleEditee) {
      const { error } = await updateVeilleComplete(veilleEditee.id, patch);
      if (error) {
        return toast({ title: "Modification impossible", description: error.message || "Vous n'avez pas les droits pour modifier cette veille.", status: "error", duration: 4000, isClosable: true });
      }
      toast({ title: "Veille modifiée", description: `« ${nom} » a été mise à jour.`, status: "success", duration: 3500, isClosable: true });
      history.push(`/admin/veilles/${veilleEditee.id}`);
      return;
    }
    const { id, error } = await addVeille(patch);
    if (error) {
      return toast({ title: "Création impossible", description: error.message || "Vous n'avez pas les droits pour créer une veille.", status: "error", duration: 4000, isClosable: true });
    }
    if (avecMatrice && id) {
      const { error: errJob } = await requestMatrice(id);
      if (errJob) {
        toast({ title: "Veille créée, génération non lancée", description: errJob.message, status: "warning", duration: 5000, isClosable: true });
      } else {
        toast({ title: "Veille créée — matrice en cours de génération", description: "Suivez l'avancement sur la page de la veille (10 à 20 minutes).", status: "success", duration: 5000, isClosable: true });
      }
      history.push(`/admin/veilles/${id}`);
      return;
    }
    toast({ title: "Veille créée", description: `« ${nom} » est enregistrée dans vos veilles programmées.`, status: "success", duration: 4000, isClosable: true });
    history.push("/admin/veilles");
  }

  const selBorder = (on) => (on ? "teal.300" : "gray.200");
  const selBg = (on) => (on ? "teal.50" : "white");

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      {/* En-tête de page */}
      <Flex align="center" mb="40px">
        <Box w="7px" h="60px" borderRadius="full" bg="teal.300" me="18px" flexShrink={0} />
        <Box>
          <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" color="gray.800" lineHeight="1.1">{edition ? "Modifier la veille" : "Nouvelle veille"}</Text>
          <Text fontSize="md" color="gray.700" mt="4px">{edition ? "Toutes les étapes sont préremplies : ajustez ce que vous voulez, puis enregistrez." : "Décrivez votre besoin, nous cadrons la veille avec vous."}</Text>
        </Box>
      </Flex>

      {/* Frise chronologique — centrée, posée sur le fond de page */}
      <Box mb="32px" px={{ md: "4px" }}>
          <Flex direction={{ base: "column", md: "row" }} align="stretch" gap={{ base: "18px", md: "0" }}>
            {STEPS.map((s, i) => {
              const done = i < step;
              const on = i <= step;
              return (
                <Flex key={s} flex={{ md: "1" }} position="relative" direction={{ base: "row", md: "column" }} align="center" gap={{ base: "14px", md: "0" }} cursor={i <= step ? "pointer" : "default"} onClick={() => i <= step && setStep(i)}>
                  {i < STEPS.length - 1 && (
                    <Box display={{ base: "none", md: "block" }} position="absolute" top="21px" left="50%" w="100%" h="3px" borderRadius="full" bg={i < step ? "teal.300" : "gray.200"} zIndex={0} />
                  )}
                  <Flex w="46px" h="46px" borderRadius="full" bg={on ? "teal.300" : "gray.100"} color={on ? "white" : "gray.400"} align="center" justify="center" fontSize="lg" fontWeight="bold" flexShrink={0} border="4px solid white" zIndex={1} boxShadow={on ? "0 4px 10px rgba(49,151,149,0.3)" : "none"}>
                    {done ? <Icon as={FaCheck} /> : i + 1}
                  </Flex>
                  <Box mt={{ md: "14px" }} px={{ md: "10px" }} textAlign={{ base: "left", md: "center" }}>
                    <Text fontSize="md" fontWeight="bold" color={on ? "gray.800" : "gray.400"}>{s}</Text>
                    <Text fontSize="xs" color="gray.500" mt="2px">{DESCS[i]}</Text>
                  </Box>
                </Flex>
              );
            })}
          </Flex>
      </Box>

      <Box bg="gray.100" borderRadius="24px" p={{ base: "16px", md: "24px" }}>
      <Grid templateColumns={{ base: "1fr", lg: "1.7fr 1fr" }} gap="24px" alignItems="start">
        {/* Contenu */}
        <Flex direction="column" gap="24px">
          <Flex direction="column" gap="24px" key={step} animation={`${fadeUp} 0.35s ease`}>
          {step === 0 && (
            <>
              <Card>
                <CardBody p={{ base: "22px", md: "30px" }}>
                  <Flex direction="column" w="100%">
                    <Flex align="center" mb="28px">
                      <IconBox bg="gray.100" w="52px" h="52px" me="18px"><Icon as={FaFileAlt} color="gray.800" h="22px" w="22px" /></IconBox>
                      <Box>
                        <Text fontSize="xl" color="gray.800" fontWeight="bold">Document de mapping</Text>
                        <Text fontSize="md" color="gray.700">La veille se construit à partir de la demande du client. Décrivez le besoin, nous en déduisons le cadrage.</Text>
                      </Box>
                    </Flex>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px" mb="22px">
                      <FormControl>
                        <FormLabel fontSize="md" fontWeight="bold" color="gray.800">Nom de la veille</FormLabel>
                        <Input value={nom} onChange={(e) => setNom(e.target.value)} placeholder="NordVPN — Réputation & cybersécurité" size="lg" h="52px" borderRadius="14px" fontSize="md" bg="gray.50" border="1px solid" borderColor="gray.200" />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="md" fontWeight="bold" color="gray.800">Client / marque suivie</FormLabel>
                        <Input value={client} onChange={(e) => setClient(e.target.value)} placeholder="NordVPN" size="lg" h="52px" borderRadius="14px" fontSize="md" bg="gray.50" border="1px solid" borderColor="gray.200" />
                      </FormControl>
                    </SimpleGrid>
                    <FormControl>
                      <Flex justify="space-between" align="center" mb="10px">
                        <FormLabel fontSize="md" fontWeight="bold" color="gray.800" mb="0">Demande initiale du client</FormLabel>
                        <Text fontSize="sm" color="gray.700" fontWeight="bold" cursor="pointer" textDecoration="underline" onClick={() => setDemande(EXEMPLE)}>Utiliser un exemple</Text>
                      </Flex>
                      <Textarea value={demande} onChange={(e) => setDemande(e.target.value)} rows={7} placeholder="Ex. : Nous voulons suivre notre réputation autour de… détecter toute mention de… surveiller les concurrents sur…" borderRadius="14px" fontSize="md" bg="gray.50" border="1px solid" borderColor="gray.200" />
                    </FormControl>
                  </Flex>
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <Flex justify="space-between" align="center" w="100%" direction={{ base: "column", md: "row" }} gap="16px">
                    <Flex align="center">
                      <IconBox bg="gray.100" w="44px" h="44px" me="14px"><Icon as={FaSearch} color="gray.700" h="18px" w="18px" /></IconBox>
                      <Box>
                        <Text fontWeight="bold" color="gray.800">Cadrage assisté</Text>
                        <Text fontSize="sm" color="gray.700">Nous proposons un point de départ ; vous gardez la main sur tout.</Text>
                      </Box>
                    </Flex>
                    <Button bg="teal.300" color="white" _hover={{ bg: "teal.400" }} borderRadius="12px" size="lg" onClick={analyser} isLoading={analyseEnCours} loadingText="Analyse en cours…" minW={{ md: "210px" }}>
                      Analyser ma demande
                    </Button>
                  </Flex>
                </CardBody>
              </Card>
            </>
          )}

          {step === 1 && (
            <>
              <Card>
                <CardBody>
                  <Flex direction="column" w="100%">
                    <Box mb="20px">
                      <Text fontSize="lg" color="gray.800" fontWeight="bold">Secteur & mots-clés</Text>
                      <Text fontSize="sm" color="gray.700">Ce sur quoi porte la veille. Complétez avec vos propres termes.</Text>
                    </Box>
                    <FormControl mb="20px">
                      <FormLabel fontSize="sm" fontWeight="bold" color="gray.800">Secteur d'activité à veiller</FormLabel>
                      <Input value={secteur} onChange={(e) => setSecteur(e.target.value)} placeholder="Ex. : Cybersécurité / Protection des données" borderRadius="12px" fontSize="sm" bg="gray.50" border="1px solid" borderColor="gray.200" />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="bold" color="gray.800">Mots-clés surveillés</FormLabel>
                      <Wrap mb="12px">
                        {motsCles.map((m) => (
                          <WrapItem key={m}>
                            <Tag borderRadius="full" bg="gray.100" color="gray.800" size="lg">
                              <TagLabel>{m}</TagLabel>
                              <TagCloseButton onClick={() => setMotsCles((p) => p.filter((x) => x !== m))} />
                            </Tag>
                          </WrapItem>
                        ))}
                        {motsCles.length === 0 && <Text fontSize="sm" color="gray.500">Aucun mot-clé pour l'instant.</Text>}
                      </Wrap>
                      <Flex gap="10px">
                        <Input value={nouveauMot} onChange={(e) => setNouveauMot(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); ajouterMot(); } }} placeholder="Ajouter un mot-clé et appuyer sur Entrée" borderRadius="12px" fontSize="sm" bg="gray.50" border="1px solid" borderColor="gray.200" />
                        <Button onClick={ajouterMot} variant="outline" borderColor="gray.300" color="gray.800" borderRadius="12px" leftIcon={<Icon as={FaPlus} />}>Ajouter</Button>
                      </Flex>
                    </FormControl>
                  </Flex>
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <Flex direction="column" w="100%">
                    <Box mb="16px">
                      <Text fontSize="lg" color="gray.800" fontWeight="bold">Canaux à surveiller</Text>
                      <Text fontSize="sm" color="gray.700">Cochez les médias à couvrir pour cette veille.</Text>
                    </Box>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12px" w="100%">
                      {CANAUX.map((c) => {
                        const on = canaux.includes(c.id);
                        return (
                          <Flex key={c.id} onClick={() => toggleCanal(c.id)} cursor="pointer" p="14px" borderRadius="14px" border="2px solid" borderColor={on ? "teal.300" : "gray.200"} bg="white" align="center" transition="all .15s">
                            <IconBox bg="gray.100" w="40px" h="40px" me="12px"><Icon as={c.icon} color="gray.500" /></IconBox>
                            <Box flex="1" minW="0">
                              <Text fontSize="sm" fontWeight="bold" color="gray.800">{c.label}</Text>
                              <Text fontSize="xs" color="gray.500" noOfLines={1}>{c.desc}</Text>
                            </Box>
                            {on && <Icon as={FaCheck} color="teal.500" ms="8px" />}
                          </Flex>
                        );
                      })}
                    </SimpleGrid>

                    <Box mt="22px" pt="22px" borderTop="1px solid" borderColor="gray.100">
                      <Text fontSize="md" fontWeight="bold" color="gray.800">Sources spécifiques à suivre (optionnel)</Text>
                      <Text fontSize="sm" color="gray.700" mb="12px">Ajoutez le nom d'une source précise à surveiller — pas besoin du lien (ex. : Maddyness).</Text>
                      <Wrap mb="12px">
                        {sources.map((s) => (
                          <WrapItem key={s}>
                            <Tag borderRadius="full" bg="teal.50" color="teal.700" size="lg">
                              <TagLabel>{s}</TagLabel>
                              <TagCloseButton onClick={() => setSources((p) => p.filter((x) => x !== s))} />
                            </Tag>
                          </WrapItem>
                        ))}
                        {sources.length === 0 && <Text fontSize="sm" color="gray.500">Aucune source spécifique pour l'instant.</Text>}
                      </Wrap>
                      <Flex gap="10px">
                        <Input value={nouvelleSource} onChange={(e) => setNouvelleSource(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); ajouterSource(); } }} placeholder="Nom de la source (ex. : Maddyness)" borderRadius="12px" fontSize="sm" bg="gray.50" border="1px solid" borderColor="gray.200" />
                        <Button onClick={ajouterSource} variant="outline" borderColor="gray.300" color="gray.800" borderRadius="12px" leftIcon={<Icon as={FaPlus} />}>Ajouter</Button>
                      </Flex>
                    </Box>
                  </Flex>
                </CardBody>
              </Card>
            </>
          )}

          {step === 2 && (
            <>
              <Card>
                <CardBody>
                  <Flex direction="column" w="100%">
                    <Box mb="16px">
                      <Text fontSize="lg" color="gray.800" fontWeight="bold">Format de veille</Text>
                      <Text fontSize="sm" color="gray.700">Comment souhaitez-vous recevoir vos retombées ? Un format est proposé par défaut.</Text>
                    </Box>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12px" w="100%">
                      {FORMATS.map((f) => {
                        const on = formatId === f.id;
                        return (
                          <Box key={f.id} onClick={() => setFormatId(f.id)} cursor="pointer" p="16px" borderRadius="14px" border="2px solid" borderColor={selBorder(on)} bg={selBg(on)} transition="all .15s">
                            <Flex justify="space-between" align="center">
                              <Text fontSize="sm" fontWeight="bold" color="gray.800">{f.label}</Text>
                              {on && <Icon as={FaCheck} color="teal.500" />}
                            </Flex>
                            <Text fontSize="xs" color="gray.700" mt="4px">{f.detail}</Text>
                          </Box>
                        );
                      })}
                    </SimpleGrid>
                  </Flex>
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <Flex direction="column" w="100%">
                    <Box mb="16px">
                      <Text fontSize="lg" color="gray.800" fontWeight="bold">Fréquence & diffusion</Text>
                      <Text fontSize="sm" color="gray.700">Rythme, jours, heure et destinataires — réglez tout librement.</Text>
                    </Box>

                    {/* Rythme d'envoi */}
                    <Box mb="22px">
                      <FormLabel fontSize="sm" fontWeight="bold" color="gray.800">Rythme d'envoi</FormLabel>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing="10px">
                        {FREQUENCES.map((f) => {
                          const on = frequenceId === f.id;
                          return (
                            <Box key={f.id} onClick={() => choisirFrequence(f.id)} cursor="pointer" p="14px" borderRadius="12px" border="2px solid" borderColor={selBorder(on)} bg={selBg(on)}>
                              <Flex justify="space-between" align="center">
                                <Text fontSize="sm" fontWeight="bold" color="gray.800">{f.label}</Text>
                                {on && <Icon as={FaCheck} color="teal.500" />}
                              </Flex>
                              <Text fontSize="xs" color="gray.700">{f.detail}</Text>
                            </Box>
                          );
                        })}
                      </SimpleGrid>
                    </Box>

                    {/* Jours d'envoi — libre, sauf en temps réel (flux continu) */}
                    {frequenceId !== "temps_reel" && (
                      <FormControl mb="22px">
                        <FormLabel fontSize="sm" fontWeight="bold" color="gray.800">Jours d'envoi</FormLabel>
                        <Flex gap="8px" wrap="wrap">
                          {JOURS.map((j) => {
                            const on = jours.includes(j.id);
                            return (
                              <Flex key={j.id} onClick={() => toggleJour(j.id)} cursor="pointer" align="center" justify="center" minW="54px" px="14px" py="10px" borderRadius="12px" border="2px solid" borderColor={on ? "teal.300" : "gray.200"} bg={on ? "teal.50" : "white"} transition="all .15s" _hover={{ borderColor: on ? "teal.300" : "gray.300" }}>
                                <Text fontSize="sm" fontWeight="bold" color={on ? "teal.700" : "gray.700"}>{j.court}</Text>
                              </Flex>
                            );
                          })}
                        </Flex>
                        {jours.length === 0 && <Text fontSize="xs" color="orange.500" mt="8px">Sélectionnez au moins un jour d'envoi.</Text>}
                      </FormControl>
                    )}

                    {/* Heure d'envoi — presets rapides + heure libre */}
                    {frequenceId !== "temps_reel" && (
                      <FormControl mb="22px">
                        <FormLabel fontSize="sm" fontWeight="bold" color="gray.800">Heure d'envoi</FormLabel>
                        <Flex gap="10px" wrap="wrap" align="center">
                          {HEURES.map((h) => {
                            const on = heure === h;
                            return (
                              <Flex key={h} onClick={() => setHeure(h)} cursor="pointer" align="center" gap="6px" px="16px" py="10px" borderRadius="12px" border="2px solid" borderColor={on ? "teal.300" : "gray.200"} bg={on ? "teal.50" : "white"} transition="all .15s" _hover={{ borderColor: on ? "teal.300" : "gray.300" }}>
                                <Icon as={FaRegClock} color={on ? "teal.500" : "gray.400"} fontSize="14px" />
                                <Text fontSize="sm" fontWeight="bold" color="gray.800">{h}</Text>
                              </Flex>
                            );
                          })}
                          <Flex align="center" gap="8px">
                            <Text fontSize="xs" color="gray.500" fontWeight="bold">ou</Text>
                            <Input type="time" value={heure} onChange={(e) => setHeure(e.target.value)} w="130px" borderRadius="12px" fontSize="sm" bg="gray.50" border="1px solid" borderColor="gray.200" />
                          </Flex>
                        </Flex>
                      </FormControl>
                    )}

                    {/* Destinataires — plusieurs possibles */}
                    <FormControl>
                      <FormLabel fontSize="sm" fontWeight="bold" color="gray.800">Destinataires</FormLabel>
                      <Wrap mb="12px">
                        {destinataires.map((d) => (
                          <WrapItem key={d}>
                            <Tag borderRadius="full" bg="gray.100" color="gray.800" size="lg">
                              <TagLabel>{d}</TagLabel>
                              <TagCloseButton onClick={() => setDestinataires((p) => p.filter((x) => x !== d))} />
                            </Tag>
                          </WrapItem>
                        ))}
                        {destinataires.length === 0 && <Text fontSize="sm" color="gray.500">Aucun destinataire pour l'instant.</Text>}
                      </Wrap>
                      <Flex gap="10px">
                        <Input type="email" value={nouveauDest} onChange={(e) => setNouveauDest(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); ajouterDest(); } }} placeholder="Ajouter un e-mail et appuyer sur Entrée" borderRadius="12px" fontSize="sm" bg="gray.50" border="1px solid" borderColor="gray.200" />
                        <Button onClick={ajouterDest} variant="outline" borderColor="gray.300" color="gray.800" borderRadius="12px" leftIcon={<Icon as={FaPlus} />}>Ajouter</Button>
                      </Flex>
                    </FormControl>
                  </Flex>
                </CardBody>
              </Card>
            </>
          )}

          {step === 3 && (
            <>
              {!fichier && (
              <Card>
                <CardBody>
                  <Flex direction="column" w="100%">
                    <Flex align={{ base: "flex-start", md: "center" }} justify="space-between" mb="16px" direction={{ base: "column", md: "row" }} gap="12px">
                      <Flex align="center">
                        <IconBox bg="gray.100" w="44px" h="44px" me="14px"><Icon as={FaFileAlt} color="gray.800" h="18px" w="18px" /></IconBox>
                        <Box>
                          <Text fontSize="lg" color="gray.800" fontWeight="bold">Document de mapping</Text>
                          <Text fontSize="sm" color="gray.700">Le document qui servira à cadrer la veille. Vous pouvez le modifier librement.</Text>
                        </Box>
                      </Flex>
                    </Flex>
                    <Textarea value={mappingDoc} onChange={(e) => { setMappingDoc(e.target.value); setMappingEdite(true); }} rows={15} fontSize="sm" fontFamily="mono" lineHeight="1.7" borderRadius="14px" bg="gray.50" border="1px solid" borderColor="gray.200" />
                  </Flex>
                </CardBody>
              </Card>
              )}

              <Card>
                <CardBody>
                  <Flex direction="column" w="100%">
                    <Box mb="14px">
                      <Text fontSize="lg" color="gray.800" fontWeight="bold">Vous avez déjà un document ?</Text>
                      <Text fontSize="sm" color="gray.700">Déposez votre propre document de mapping ; il remplacera celui généré ci-dessus.</Text>
                    </Box>
                    {fichier ? (
                      <Flex align="center" justify="space-between" p="16px" borderRadius="14px" border="2px solid" borderColor="teal.200" bg="teal.50" gap="12px">
                        <Flex align="center" gap="12px" minW="0">
                          <IconBox bg="white" w="40px" h="40px"><Icon as={FaFilePdf} color="teal.500" /></IconBox>
                          <Box minW="0">
                            <Text fontSize="sm" fontWeight="bold" color="gray.800" isTruncated>{fichier}</Text>
                            <Text fontSize="xs" color="gray.600">Document importé — utilisé pour cette veille</Text>
                          </Box>
                        </Flex>
                        <Button size="sm" variant="ghost" color="gray.600" flexShrink={0} onClick={() => setFichier(null)}>Retirer</Button>
                      </Flex>
                    ) : (
                      <Flex direction="column" align="center" justify="center" py="32px" px="20px" borderRadius="14px" border="2px dashed" borderColor="gray.200" bg="gray.50" cursor="pointer" transition="all .15s" _hover={{ borderColor: "teal.300", bg: "teal.50" }} onClick={() => fileRef.current && fileRef.current.click()}>
                        <IconBox bg="white" w="46px" h="46px" mb="12px"><Icon as={FaUpload} color="gray.500" /></IconBox>
                        <Text fontSize="sm" fontWeight="bold" color="gray.800">Glissez votre document ici ou cliquez pour parcourir</Text>
                        <Text fontSize="xs" color="gray.500" mt="4px">PDF, Word ou texte — 10 Mo max</Text>
                      </Flex>
                    )}
                    <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.txt" style={{ display: "none" }} onChange={onFichier} />
                  </Flex>
                </CardBody>
              </Card>
            </>
          )}

          </Flex>

          {/* Navigation — visible après l'analyse, ou d'emblée en mode édition */}
          {(step > 0 || edition) && (
            <Flex justify="space-between" align="center" animation={`${fadeUp} 0.3s ease`}>
              {step > 0 ? (
                <Button variant="ghost" color="gray.700" onClick={() => setStep((s) => Math.max(0, s - 1))} leftIcon={<Icon as={FaArrowLeft} />}>Précédent</Button>
              ) : <Box />}
              {step < STEPS.length - 1 ? (
                <Button bg="teal.300" color="white" _hover={{ bg: "teal.400" }} borderRadius="12px" onClick={continuer} rightIcon={<Icon as={FaArrowRight} />}>Continuer</Button>
              ) : (
                <Flex gap="10px" wrap="wrap">
                  {!edition && (
                    <Button variant="outline" borderColor="teal.300" color="teal.600" _hover={{ bg: "teal.50" }} borderRadius="12px" size="lg" onClick={() => creer(false)} leftIcon={<Icon as={FaPaperPlane} />}>Créer la veille</Button>
                  )}
                  <Button bg="teal.300" color="white" _hover={{ bg: "teal.400" }} borderRadius="12px" size="lg" onClick={() => creer(!edition)} leftIcon={<Icon as={FaFileAlt} />}>{edition ? "Enregistrer les modifications" : "Créer et générer la matrice"}</Button>
                </Flex>
              )}
            </Flex>
          )}
        </Flex>

        {/* Aperçu live — panneau sombre */}
        <Box>
          <Card position={{ lg: "sticky" }} top="90px">
            <CardBody>
              <Flex direction="column" w="100%" gap="16px">
                <Flex align="center">
                  <Box w="5px" h="42px" borderRadius="full" bg="teal.300" me="12px" flexShrink={0} />
                  <Box>
                    <Text fontSize="md" color="gray.800" fontWeight="bold">Aperçu de la veille</Text>
                    <Text fontSize="sm" color="gray.700">Se met à jour au fil du cadrage.</Text>
                  </Box>
                </Flex>
                <ApercuLigne label="Nom" value={nom || "—"} />
                <ApercuLigne label="Client" value={client || "—"} />
                <ApercuLigne label="Secteur" value={secteur || "—"} />
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb="8px">Mots-clés</Text>
                  {motsCles.length ? (
                    <Wrap>{motsCles.map((m) => (<WrapItem key={m}><Badge bg="gray.100" color="gray.800" borderRadius="8px" px="8px">{m}</Badge></WrapItem>))}</Wrap>
                  ) : <Text fontSize="sm" color="gray.500">—</Text>}
                </Box>
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb="8px">Canaux</Text>
                  {canaux.length ? (
                    <Wrap>{canaux.map((c) => (<WrapItem key={c}><Badge bg="gray.100" color="gray.800" borderRadius="8px" px="8px">{canal(c).label}</Badge></WrapItem>))}</Wrap>
                  ) : <Text fontSize="sm" color="gray.500">—</Text>}
                </Box>
                {sources.length > 0 && (
                  <Box>
                    <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" mb="8px">Sources spécifiques</Text>
                    <Wrap>{sources.map((s) => (<WrapItem key={s}><Badge bg="teal.50" color="teal.700" borderRadius="8px" px="8px">{s}</Badge></WrapItem>))}</Wrap>
                  </Box>
                )}
                <SimpleGrid columns={2} spacing="12px" pt="12px" borderTop="1px solid" borderColor="gray.100">
                  <ApercuLigne label="Format" value={analyzed ? ((fmt(formatId) || {}).label || "—") : "—"} />
                  <ApercuLigne label="Fréquence" value={analyzed ? ((freq(frequenceId) || {}).label || "—") : "—"} />
                </SimpleGrid>
                <ApercuLigne label="Envoi" value={frequenceId === "temps_reel" ? "En continu" : `${jours.map((j) => (jour(j) || {}).court).filter(Boolean).join(", ") || "—"} · ${heure}`} />
                <ApercuLigne label="Destinataires" value={destinataires.length ? `${destinataires.length} destinataire${destinataires.length > 1 ? "s" : ""}` : "—"} />
                <ApercuLigne label="Document de mapping" value={fichier ? fichier : (mappingEdite ? "Modifié à la main" : (mappingDoc.trim() ? "Généré" : "—"))} />
              </Flex>
            </CardBody>
          </Card>
        </Box>
      </Grid>
      </Box>
    </Flex>
  );
}

function ApercuLigne({ label, value }) {
  return (
    <Box>
      <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">{label}</Text>
      <Text fontSize="sm" fontWeight="bold" color="gray.800" mt="2px">{value}</Text>
    </Box>
  );
}
